import {
  createContext, useCallback, useContext, useEffect, useId, useLayoutEffect, useRef, useState,
  type PointerEvent, type ReactNode, type RefObject,
} from "react";
import { createPortal } from "react-dom";
import { T, useT } from "../i18n";
import { Knopf, Meldung } from "./index";
import { useSchliessanfrage } from "../schale/schliessanfrage";
import { tastaturSchliessen } from "../schale/tastatur";
import "../stile/blattvonunten.css";

/* ══════════════════════════════════════════════════════════════════
   Das Blatt von unten — die eine Fläche, die am Telefon hereinkommt.

   Luca, 16.09.2026: *„mit Bottom Sheets beim Angebotschreiben, das muss
   mobile-optimiert sein, sodass das vernünftig funktioniert."* Und am
   17.09.2026: *„Alle Bottom Sheets, die du baust, müssen auch
   runterziehbar sein — die sind nicht runterziehbar."*

   ── Es war zweimal dasselbe ───────────────────────────────────────

   Bis zum 17.09.2026 gab es diesen Baustein **doppelt**: hier als
   `Blattvonunten` (Preis und Zeitraum) und noch einmal als
   `masken/handy/Blatt.tsx` (Angebot, Notiz, Zeilen). Beide legten sich
   von unten über den `#maskenraum`, beide hatten einen Griff, beide
   schlossen mit `Esc` — und beide hatten etwas, das dem anderen fehlte:
   hier die Fokusfalle und der gesperrte Grund, dort der Schritt zurück
   und der Fuß, der stehen bleibt. Die Ziehgeste hatte keiner von
   beiden. Sie sind zusammengelegt; `Blatt.tsx` ist weg.

   ── Warum nicht das Deckblatt ─────────────────────────────────────

   Das legt sich mittig über den Arbeitsbereich und ist für den
   Schreibtisch gebaut: Kopfzeile oben, Schließen oben rechts. Am
   Telefon ist oben rechts die Ecke, die eine Hand am schlechtesten
   erreicht — und eine Maske, die den ganzen Schirm nimmt, verdeckt
   genau die Zahl, für die man sie geöffnet hat.

   **Das Ergebnis bleibt stehen.** Das Blatt ist so hoch wie das, was
   darin steht — bis zu seinem Rastpunkt (`rast`); darüber sieht man
   weiter, was unten gerade gilt.

   ── Das Herunterziehen ────────────────────────────────────────────

   Ein Blatt, das von unten kommt, schiebt man nach unten wieder weg.
   Das ist keine Verzierung: Am Telefon ist der Balken oben am Blatt
   genau das Versprechen, und wer daran zieht und nichts passiert, hält
   die Maske für hängengeblieben.

   * **Angefasst wird am Griff und an der Fläche** — überall, wo nicht
     schon etwas anderes an der Reihe ist: Ein Knopf, ein Feld, eine
     Liste, die selbst gerollt ist, bleiben ihre eigene Sache.
   * **Es folgt dem Finger**, eins zu eins und nur nach unten. Nach oben
     gibt es nichts zu holen.
   * **Es schließt ab einem Drittel der Höhe** — oder früher, wenn der
     Wurf schnell genug war. Wer schnippt, meint zu.
   * **Sonst federt es zurück**, in 180 ms.
   * **`prefers-reduced-motion` schaltet die Federung ab**, nicht die
     Geste: Das Blatt springt dann an seinen Platz, statt zu gleiten.

   Gearbeitet wird mit Zeigerereignissen (Pointer Events) — eine
   Schnittstelle für Finger, Stift und Maus, keine Bibliothek.

   ── Was der Baustein sonst mitbringt ──────────────────────────────

   `Esc`, ein Griff auf den Grund, Fokus auf das Blatt und zurück (nie
   in ein Feld — siehe unten), Tab im
   Kreis, kein Rollen der Seite dahinter, der sichere Rand unten
   (`env(safe-area-inset-bottom)`), ein Schritt zurück im Kopf und ein
   Fuß, der stehen bleibt, während der Leib rollt.

   Er hängt im Arbeitsbereich (`#maskenraum`), nicht im `body` —
   dieselbe Stelle wie das Deckblatt, damit beide dieselbe Ebene haben.

   Klassen: `bvu-*`, gegen alle Stile geprüft.
   ══════════════════════════════════════════════════════════════════ */

/** Wo das Blatt hineingehängt wird — derselbe Arbeitsbereich wie beim Deckblatt. */
const ZIEL = "maskenraum";

const FOKUSSIERBAR =
  'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), '
  + 'textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** Was seine eigene Berührung behält — daran beginnt keine Ziehbewegung. */
const EIGENE_SACHE =
  'input, textarea, select, a[href], [contenteditable="true"], [role="slider"]';

/** Ab hier gilt es als Zug und nicht als Tipper. */
const SCHWELLE_ZUG = 8;
/** Der Anteil der Blatthöhe, ab dem losgelassen zumacht. */
const ANTEIL_ZU = 0.32;
/** Und die Geschwindigkeit, die auch bei kurzem Weg zumacht (px je ms). */
const SCHWUNG_ZU = 0.6;

type Zug = {
  zeiger: number;
  start: number;
  zuletzt: number;
  wann: number;
  tempo: number;
  weg: number;
  zieht: boolean;
  /**
   * Die Höhe des Blattes, **einmal beim Anfassen gemessen** (21.09.2026).
   *
   * Sie stand vorher nicht hier, und `setzen` las sie bei **jedem**
   * `pointermove` aus dem DOM — zwischen zwei Schreibvorgängen am selben
   * Element (`transform` setzen, `offsetHeight` lesen, `opacity` setzen).
   * Das ist ein erzwungenes Layout je Bild, und es erklärt das
   * gelegentliche Flackern: Liefert die Messung mitten in der
   * Eingangsanimation `0`, rechnet die Verdunkelung mit `hoehe = 1` und
   * springt in einem Bild auf ihren Tiefstwert.
   *
   * Sie ändert sich während eines Zugs ohnehin nicht — das Blatt wird
   * verschoben, nicht größer.
   */
  hoehe: number;
  /** Löst die Schwungbremse (siehe `schwungBremsen`) — `null`, wo keine liegt. */
  bremse: (() => void) | null;
};

/* ── Ein Formular im Blatt meldet seinen Entwurf selbst (19.09.2026) ──

   Die Prop `schmutzig` reicht, solange der Entwurf dort liegt, wo auch das
   Blatt steht (Person, Einsatzort, Notiz). Die meisten Formulare am Telefon
   sind aber die des Schreibtischs, ins Blatt gestellt: die Preispflege, der
   Textbaustein, die Bearbeitung eines Benutzers in der Unterzeile einer
   Tabelle. Ihr Entwurf liegt zwei, drei Ebenen tiefer, und eine Prop durch
   jede Ebene wäre an jedem dieser Formulare eine Änderung, die am
   Schreibtisch nichts bedeutet.

   **Also meldet das Formular selbst**: `useBlattSchmutzig(geaendert)`. Steht
   es in einem Blatt, fragt das Blatt beim Wegziehen nach; steht es am
   Schreibtisch in keinem, geht die Meldung ins Leere und nichts ändert sich.
   Mehrere Formulare in einem Blatt melden jedes für sich — die Frage kommt,
   sobald eines davon etwas trägt. Wird das Formular abgebaut (gespeichert
   und geschlossen, anderer Inhalt im Blatt), nimmt es seine Meldung mit. */
const SchmutzMelden = createContext<((wer: string, schmutzig: boolean) => void) | null>(null);

/** Meldet dem umgebenden `Blattvonunten`, dass ein Entwurf ungesichert ist. */
export function useBlattSchmutzig(schmutzig: boolean) {
  const melden = useContext(SchmutzMelden);
  const wer = useId();
  useEffect(() => {
    if (!melden) return undefined;
    melden(wer, schmutzig);
    return () => melden(wer, false);
  }, [melden, wer, schmutzig]);
}

/**
 * Für Formulare ohne eigenen Änderungsmerker: Der Stand beim Öffnen wird
 * festgehalten, und was davon abweicht, ist ein Entwurf. Gibt `gesichert`
 * zurück — es nimmt den jetzigen Stand als neuen Anfang, für Formulare, die
 * nach dem Speichern offen bleiben. Wer nach dem Speichern schließt, braucht
 * es nicht: Mit dem Formular geht seine Meldung.
 *
 * Der Stand wird als JSON verglichen — Zeichenketten, Zahlen, Listen davon.
 */
export function useBlattEntwurf(stand: unknown): () => void {
  const jetzt = JSON.stringify(stand);
  const [anfang, setzeAnfang] = useState(jetzt);
  useBlattSchmutzig(jetzt !== anfang);
  return useCallback(() => setzeAnfang(jetzt), [jetzt]);
}

export function Blattvonunten({
  offen, schliessen, titel, unter, zurueck, fuss, form: festeForm, rast = "klein",
  schmutzig = false, children,
}: {
  offen: boolean;
  /** Wird gerufen, wenn das Blatt wirklich zugeht — nach der Rückfrage, nicht davor. */
  schliessen: () => void;
  titel: string;
  /** Die zweite Zeile im Kopf — der Zusammenhang, nicht eine Erklärung. */
  unter?: string | undefined;
  /** Ein Schritt im Blatt selbst: „Gerät wählen" → „Zeitraum". */
  zurueck?: (() => void) | undefined;
  /** Was unten stehen bleibt, während der Leib rollt. */
  fuss?: ReactNode;
  /**
   * Die Form, statt der am Inhalt gemessenen.
   *
   * **Der Regelfall bleibt die Messung** („Die Regel für Blätter am Telefon"
   * unten): Wer ein Tippfeld trägt, wird Vollbild. Es gibt aber Blätter, die
   * ein Tippfeld tragen und trotzdem von unten kommen sollen — das
   * Nachschlagen (Luca, 18.09.2026: *„macht das auch als Bottom Sheet,
   * sodass ich es runterziehen kann"*) und die Notiz (*„da auch ein kleines
   * Bottom Sheet, nicht so ein großes wieder"*). Dort ist der Streifen Maske
   * über dem Blatt kein Verlust, sondern die Auskunft darüber, dass sich
   * etwas herunterziehen lässt. Wer hier etwas setzt, hat diesen Grund.
   */
  form?: Blattform | undefined;
  /**
   * Der Rastpunkt — wie hoch das Blatt **höchstens** wird.
   *
   * **Die Höhe ist der Inhalt** („Ein Blatt ist so hoch wie das, was darin
   * steht"): Fünf Zeilen sind fünf Zeilen hoch, und die Maske darunter bleibt
   * zu sehen. Der Rastpunkt ist nur der Deckel für den Fall, dass der Inhalt
   * länger ist als das, was ein Daumen erreicht.
   *
   * | | Deckel | wofür |
   * |---|---|---|
   * | `"klein"` (Vorgabe) | 75 % | Auskunft, Auswahl, kurze Erfassung, das Mehr-Blatt |
   * | `"gross"` | 90 % | was eine Fläche braucht: der Kalender im Zeitraum |
   *
   * Wer `"gross"` setzt, hat einen Grund, der in der Maske steht.
   *
   * **Ein Blatt, das ein Formular trägt, setzt `"gross"`.** Nachgemessen am
   * 18.09.2026 an den Ansprechpartnern der Kundenakte: Bei `klein` bleiben
   * nach Kopf und Fuß rund 164 px für sechs bis sieben Felder. Das ist keine
   * Frage des Geschmacks, sondern eine Rechnung — und sie fällt für jedes
   * Formular gleich aus.
   */
  rast?: Blattrast | undefined;
  /**
   * Steht im Blatt etwas, das verloren ginge? Dann fragt das Schließen nach.
   *
   * *Ergänzt am 18.09.2026.* Bis dahin kannte nur das Deckblatt diese Frage,
   * und das Blatt von unten schloss kommentarlos. Seitdem laufen Formulare
   * darüber — Ansprechpartner, Einsatzort —, und **am Telefon ist ein halb
   * getippter Ansprechpartner mit einem Zug weg.** Genau der Zug ist die
   * Hauptgeste des Blattes.
   *
   * **Alle fünf Auswege gehen denselben Weg:** Griff, Kreuz, `Esc` samt
   * Zurückgeste, die Ziehgeste und der Tipper auf den Grund. Einer, der
   * daran vorbeigeht, ist derselbe Datenverlust an einer anderen Stelle.
   *
   * Die Frage ist dieselbe wie im Deckblatt — dieselben Wörter, dieselben
   * beiden Knöpfe, dieselben i18n-Schlüssel (`deckblatt.verwerfen*`). Zwei
   * Formulierungen für dieselbe Frage wären zwei Bedeutungen.
   *
   * Wird das Formular wieder leer, ist die Frage von selbst erledigt.
   */
  schmutzig?: boolean;
  children: ReactNode;
}) {
  const t = useT();
  const id = useId();
  const blatt = useRef<HTMLDivElement>(null);
  const schirm = useRef<HTMLDivElement>(null);
  const leib = useRef<HTMLDivElement>(null);
  const [ziel, setzeZiel] = useState<HTMLElement | null>(null);

  /* Welcher Ausgang die Frage gestellt hat: das Schließen oder der Schritt
     zurück im Blatt. „Verwerfen" nimmt dann genau diesen Weg. */
  const [fragt, setzeFragt] = useState<null | "zu" | "zurueck">(null);

  /* Was die Formulare darin melden (`useBlattSchmutzig`) — je Formular ein
     Eintrag, solange es etwas Ungesichertes trägt. */
  const [gemeldet, setzeGemeldet] = useState<ReadonlySet<string>>(() => new Set());
  const melden = useCallback((wer: string, wert: boolean) => {
    setzeGemeldet((alt) => {
      if (alt.has(wer) === wert) return alt;
      const neu = new Set(alt);
      if (wert) neu.add(wer); else neu.delete(wer);
      return neu;
    });
  }, []);
  const traegtEntwurf = schmutzig || gemeldet.size > 0;

  useEffect(() => { setzeZiel(document.getElementById(ZIEL)); }, []);

  const zu = useCallback(() => schliessen(), [schliessen]);

  /* **Der eine Ausgang.** Jeder Weg hinaus ruft `versuchen` und nicht `zu` —
     der Griff, das Kreuz, `Esc` und die Zurückgeste, die Ziehgeste und der
     Tipper auf den Grund. Steht etwas im Blatt, kommt die Frage; sonst geht
     es zu wie bisher.

     Nicht `window.confirm`: Das ist ein fremdes Fenster in fremder Sprache,
     das nicht so aussieht wie der Rest — dieselbe Überlegung wie im
     Deckblatt, und dieselbe Antwort: Die Frage steht im Blatt selbst.

     **Und jeder Weg hinaus nimmt zuerst die Tastatur mit** (20.09.2026,
     `schale/tastatur.ts`, „Die Tastatur wird mitgenommen"). Luca: *„Es ist
     immer dann, wenn man das Bottom Sheet runterzieht, und nicht X."* Der
     Unterschied zwischen beiden ist nicht die Geste, sondern der Fokus: Ein
     Tipp auf Kreuz, Griff oder Grund liegt **neben** dem Feld, und WebKit
     nimmt ihm dabei von selbst den Fokus. Ein Zug liegt nirgendwo — das Feld
     fällt stumm aus dem Baum, die Tastatur bleibt stehen, und der nächste
     Tipp trifft sie statt den Knopf. Hier steht es, weil hier **alle** fünf
     Auswege durchkommen; an der Geste allein wäre es die vierte Abschrift.

     Es steht vor der Nachfrage und nicht dahinter: Eine Frage, die hinter der
     Tastatur steht, ist keine. */
  const versuchen = useCallback(() => {
    tastaturSchliessen(blatt.current);
    if (traegtEntwurf) setzeFragt("zu");
    else zu();
  }, [traegtEntwurf, zu]);

  /* **Der Schritt zurück ist auch ein Ausgang** (19.09.2026) — für das
     Formular, das dabei verschwindet: von der Pflege eines Preises zurück
     zur Auskunft, von einer Leistung zurück zur Liste. Er fragt dieselbe
     Frage wie das Schließen. */
  const zurueckVersuchen = useCallback(() => {
    if (!zurueck) return;
    tastaturSchliessen(blatt.current);
    if (traegtEntwurf) setzeFragt("zurueck");
    else zurueck();
  }, [traegtEntwurf, zurueck]);

  const verwerfen = () => {
    const weg = fragt;
    setzeFragt(null);
    if (weg === "zurueck") zurueck?.();
    else zu();
  };

  // Ist das Formular wieder leer, ist die Frage erledigt.
  useEffect(() => { if (!traegtEntwurf) setzeFragt(null); }, [traegtEntwurf]);

  // Ein Blatt, das neu aufgeht, fängt ohne offene Frage an.
  useEffect(() => { if (!offen) setzeFragt(null); }, [offen]);

  /* ── Der Fokus geht auf das Blatt, nie in ein Feld (18.09.2026) ──

     Luca, nach dem Durchklicken der Handy-Ansicht: *„Immer wenn wir in die
     Datepicker oder so was reingehen, wird auch sofort die Tastatur
     getriggert, was wir natürlich nicht wollen. … auf jeden Fall nicht die
     Tastatur triggern, weil das einen verrückt macht."*

     Bis hierher suchte das Blatt beim Öffnen das erste Eingabefeld. Am
     Schreibtisch war das ein Gewinn — tippen ohne Klick —, am Telefon war
     es der Grund, warum über der halben Fläche sofort die Tastatur stand,
     bevor irgendjemand etwas lesen konnte: Im Zeitraum fiel der Fokus auf
     das Datumsfeld, und mit ihm ging der Kalender auf **und** die Tastatur.

     Jetzt bekommt der Dialog selbst den Fokus (`tabIndex={-1}`): Ein
     Vorleser liest seinen Titel, `Tab` läuft von oben hinein, `Esc`
     schließt weiter — und wer tippen will, tippt das Feld an. Beim
     Schließen kehrt der Fokus dorthin zurück, wo er herkam.

     Damit ist `data-kein-erstfokus` erledigt: Es gibt keinen Erstfokus
     mehr, den ein Block zurücknehmen müsste.

     `ziel` steht mit in der Liste (20.09.2026): Ein Blatt, das schon offen
     in den Baum kommt — der Kalender aus `bausteine/Zeitraum.tsx`, das
     Tagesblatt der Wiedervorlage —, hat beim ersten Durchlauf noch keinen
     Ort und damit kein Element, das den Fokus nehmen könnte. Erst mit dem
     Ort gibt es das Blatt; dann bekommt es ihn. */
  useEffect(() => {
    if (!offen || !ziel) return undefined;
    const vorher = document.activeElement as HTMLElement | null;
    blatt.current?.focus();
    return () => { fokusZurueck(vorher); };
  }, [offen, ziel]);

  /* Die Seite dahinter rollt nicht mit. Ohne das schiebt sich am Telefon
     die Maske unter dem Blatt weg, sobald jemand in der Liste wischt —
     und nach dem Schließen steht man woanders. */
  useEffect(() => {
    if (!offen) return undefined;
    const vorher = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = vorher; };
  }, [offen]);

  /* **`Esc`, die Zurückgeste auf Android und der Zurück-Knopf** schließen
     (`schale/schliessanfrage.ts`, 40-mobiles-grundgeruest.md Schritt 7) —
     ohne Eintrag im Verlauf. Liegt ein zweites Blatt darüber, schließt die
     Geste das obere. */
  const istOben = useSchliessanfrage(offen, versuchen);

  /* Tab bleibt im Blatt — im obersten. */
  useEffect(() => {
    if (!offen) return undefined;
    const taste = (e: KeyboardEvent) => {
      if (!istOben()) return;
      if (e.key !== "Tab" || !blatt.current) return;
      const alle = Array.from(blatt.current.querySelectorAll<HTMLElement>(FOKUSSIERBAR))
        .filter((el) => el.offsetParent !== null);
      const erstes = alle[0];
      const letztes = alle[alle.length - 1];
      if (!erstes || !letztes) return;
      if (e.shiftKey && document.activeElement === erstes) { e.preventDefault(); letztes.focus(); }
      else if (!e.shiftKey && document.activeElement === letztes) { e.preventDefault(); erstes.focus(); }
    };
    document.addEventListener("keydown", taste, true);
    return () => document.removeEventListener("keydown", taste, true);
  }, [offen, istOben]);

  /* ── Die Ziehbewegung und die Form (17.09.2026) ──
     Beide leben seit dem Abend als Haken unten in dieser Datei: Das
     Deckblatt zeichnet sich am Telefon ebenfalls von unten, wenn es eine
     kurze Auskunft trägt, und muss sich dann genauso wegziehen lassen. */
  /* **Ein Textfeld macht das Blatt nicht mehr zum Vollbild** (19.09.2026).
     Luca: *„Das Bottom Sheet geht bis ganz oben. Macht das bitte kleiner.
     Ein normaler Bottom Sheet, wie wir es sonst auch schon einsetzen."*
     Das Blatt bleibt unten, bekommt eine Mindesthöhe (`data-tippen`,
     `stile/blattvonunten.css`) und sitzt bei offener Tastatur über ihr
     (`stile/telefon.css`). Vollbild ist es nur noch, wo eine Maske es
     ausdrücklich verlangt (`form="vollbild"`). Das Deckblatt behält seine
     Regel (`useBlattform`). */
  const tippen = useTippfeld(offen, blatt, ziel);
  const form = festeForm ?? "unten";
  const griffe = useZiehen({ blatt, schirm, leib, zu: versuchen, nurAmGriff: form === "vollbild" });

  // Beim Öffnen steht das Blatt wieder oben — ein Blatt, das halb
  // heruntergezogen wieder aufgeht, wäre ein Fehler, den niemand sucht.
  const { setzen } = griffe;
  useEffect(() => { if (offen) setzen(0, false); }, [offen, setzen]);

  if (!offen || !ziel) return null;

  return createPortal(
    <SchmutzMelden.Provider value={melden}>
    <div
      className="bvu-schirm"
      data-form={form}
      data-rast={rast}
      data-tippen={tippen ? "ja" : undefined}
      ref={schirm}
      // Nur der Grund selbst schließt — nicht ein Griff, der im Blatt
      // beginnt und über den Rand hinausrutscht.
      onMouseDown={(e) => { if (e.target === e.currentTarget) versuchen(); }}
    >
      <div
        className="bvu-blatt"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${id}-titel`}
        ref={blatt}
        tabIndex={-1}
        onPointerDown={griffe.anfassen}
        onPointerMove={griffe.ziehen}
        onPointerUp={griffe.loslassen}
        onPointerCancel={griffe.loslassen}
      >
        {/* Der Griff ist der Knopf: Der Balken sieht aus wie das, was man
            am Telefon nach unten zieht, er IST die Fläche, an der gezogen
            wird — und ein Tipper darauf schließt. 48 px hoch, über die
            ganze Breite. Das Kreuz im Kopf darunter ist der zweite Weg
            hinaus; warum es beide gibt, steht dort. */}
        <button
          type="button"
          className="bvu-griff"
          onClick={versuchen}
          aria-label={t("allgemein.schliessen")}
          title={t("allgemein.schliessen")}
        >
          <span className="bvu-griff-balken" aria-hidden="true" />
        </button>

        <header className="bvu-kopf">
          {zurueck ? (
            <button
              type="button"
              className="bvu-zurueck"
              onClick={zurueckVersuchen}
              aria-label={t("handyBlatt.zurueck")}
              title={t("handyBlatt.zurueck")}
            >
              <span aria-hidden="true">{t("handyBlatt.pfeilLinks")}</span>
            </button>
          ) : null}
          <div className="bvu-kopf-text">
            <h3 id={`${id}-titel`}>{titel}</h3>
            {unter ? <p>{unter}</p> : null}
          </div>
          {/* **Griff und Kreuz zugleich** (18.09.2026). Luca, über das
              Nachschlagen am Telefon: *„Dieser Schließen-Knopf — macht das
              auch als Bottom Sheet, sodass ich es runterziehen kann. Oben
              kannst du meinetwegen ein X in die Ecke machen."*

              Hier stand bis dahin der umgekehrte Satz: Ein Kreuz neben dem
              Griff sei ein zweites Ziel für dieselbe Sache. Das stimmt nur,
              wenn beide dasselbe versprechen — tun sie nicht. **Der Griff ist
              die Geste, das Kreuz ist der Knopf.** Wer die Geste nicht kennt
              (und im Vollbild, wo nur der Griff zieht, kennt sie fast
              niemand), braucht etwas, das aussieht wie ein Ausgang. Dieselbe
              Aufteilung tragen die Muster, an denen wir uns messen: Griff
              mittig, Titel links, Kreuz rechts. */}
          <button
            type="button"
            className="bvu-zu"
            onClick={versuchen}
            aria-label={t("allgemein.schliessen")}
            title={t("allgemein.schliessen")}
          >
            <span aria-hidden="true">×</span>
          </button>
        </header>

        {/* **Die Rückfrage steht im Blatt, über dem, was sie betrifft** —
            dieselbe Meldung, dieselben Wörter und dieselben beiden Knöpfe
            wie im Deckblatt (`bausteine/Deckblatt.tsx`, `.deck-frage`). Sie
            ist nicht als zweites Blatt gebaut: Ein Blatt über einem Blatt
            verdeckt genau das Formular, über dessen Verbleib es geht — und
            wer „Weiter bearbeiten" drückt, soll sehen, wohin er
            zurückkommt. Der Leib bleibt darunter stehen, mit allem, was
            schon getippt ist. */}
        {fragt ? (
          <div className="bvu-frage">
            <Meldung
              art="acht"
              titel="deckblatt.verwerfenFrage"
              knoepfe={(
                <>
                  <Knopf klein art="haupt" onClick={() => setzeFragt(null)}>
                    {t("deckblatt.weiterBearbeiten")}
                  </Knopf>
                  <Knopf klein onClick={verwerfen}>{t("deckblatt.verwerfen")}</Knopf>
                </>
              )}
            >
              <T k="deckblatt.verwerfenSatz" />
            </Meldung>
          </div>
        ) : null}

        <div className="bvu-leib" ref={leib}>{children}</div>
        {fuss ? <div className="bvu-fuss">{fuss}</div> : null}
      </div>
    </div>
    </SchmutzMelden.Provider>,
    ziel,
  );
}

/* ══════════════ Die Regel für Blätter am Telefon (17.09.2026) ══════════════

   Das Deckblatt war am Telefon Vollbild, 40-mobiles-grundgeruest.md sah
   ein Blatt von unten vor, und beide Bausteine entschieden es jeder für
   sich. Jetzt gilt eine Regel für beide, und sie fragt den Inhalt — keine
   Maske muss dafür etwas anders schreiben:

   | Inhalt | am Telefon |
   |---|---|
   | ein **Formular** — mindestens ein Feld, in das man Text tippt | Vollbild |
   | eine **lange Fläche** — das Deckblatt ohne `kompakt` | Vollbild |
   | eine **kurze Auskunft, eine Auswahl** — alles Übrige | Blatt von unten, höchstens 80 % hoch |

   **Warum das Tippfeld die Grenze ist:** Geht die Tastatur auf, bleiben
   einem Blatt von 80 % am iPhone rund 350 px, abzüglich Kopf und Fuß — zu
   wenig für ein zweites Feld, und die Maske darüber sieht man ohnehin
   nicht mehr. Ein Datum, eine Zahl, eine Auswahl holen keine volle
   Tastatur und bleiben unten; das Ergebnis darüber bleibt sichtbar, und
   dafür ist das Blatt von unten da.

   Auf Tablet und Schreibtisch ändert die Regel nichts: Dort gilt das
   Deckblatt mittig, das Blatt von unten in Telefonbreite. */

/**
 * Ein Feld, in das man Text tippt — **es holt die volle Tastatur.**
 *
 * Der zweite Halbsatz ist die eigentliche Regel; die Liste der Typen ist nur
 * ihre Abschrift. Am 18.09.2026 fielen beide auseinander: Im Angebot machte
 * ein **Mengenfeld** ein Blatt mit zwei Feldern zum ganzen Schirm. Es steht
 * als `<input inputMode="decimal">` ohne `type` da — nach der Abschrift ein
 * Textfeld (`input:not([type])`), nach der Regel keines: `inputmode` sagt dem
 * Telefon ausdrücklich, dass es den Ziffernblock zeigen soll und nicht die
 * Tastatur.
 *
 * **Also fragt die Liste jetzt beides.** Wo `inputmode` einen Block ansagt —
 * `numeric`, `decimal`, `tel` oder gar keine Tastatur (`none`) —, ist das
 * Feld kein Tippfeld, egal was `type` sagt. Wo nichts angesagt ist, bleibt
 * es beim Typ. Das ist keine Ausnahme für das Angebot, sondern die Regel,
 * genauer geschrieben.
 */
const OHNE_VOLLE_TASTATUR =
  ':not([inputmode="numeric"], [inputmode="decimal"], [inputmode="tel"], [inputmode="none"])';

export const TIPPFELD = [
  'input:not([type])', 'input[type="text"]', 'input[type="search"]', 'input[type="email"]',
  'input[type="tel"]', 'input[type="url"]', 'input[type="password"]', 'textarea',
].map((feld) => feld + OHNE_VOLLE_TASTATUR).join(", ") + ', [contenteditable="true"]';

/**
 * Den Fokus dorthin zurückgeben, wo er vor dem Blatt stand — **außer in ein
 * Tippfeld am Finger** (19.09.2026).
 *
 * Luca: *„Man muss ganz oft zweimal auf den Button klicken … prüfen, warum der
 * Fokus so ist."* Am iPhone bekommt ein Knopf beim Antippen keinen Fokus; was
 * beim Öffnen eines Blattes den Fokus hielt, war deshalb oft das **Feld**, in
 * dem man vorher getippt hatte. Beim Schließen sprang er dorthin zurück — und
 * weil das Schließen selbst ein Tipp ist, holte Safari dafür die Tastatur
 * herauf. Die untere Leiste trat zur Seite, der Maskenraum schrumpfte, und der
 * nächste Tipp traf, was eben noch woanders stand.
 *
 * Mit der Maus und mit `Tab` bleibt es, wie es war: Wer mit der Tastatur
 * arbeitet, landet wieder auf dem Knopf oder Feld, von dem er kam. Am Finger
 * wird dabei auch nicht gerollt (`preventScroll`) — der Fokus soll zurück,
 * nicht die Maske verschieben.
 */
export function fokusZurueck(vorher: HTMLElement | null): void {
  if (!vorher || typeof vorher.focus !== "function") return;
  const finger = typeof window !== "undefined" && window.matchMedia?.("(pointer: coarse)").matches === true;
  if (!finger) { vorher.focus(); return; }
  if (!vorher.isConnected || vorher.matches(`${TIPPFELD}, select`)) return;
  vorher.focus({ preventScroll: true });
}

export type Blattform = "unten" | "vollbild";

/** Der Rastpunkt eines Blattes von unten — siehe die Eigenschaft `rast`. */
export type Blattrast = "klein" | "gross";

/**
 * Die Form eines offenen Blattes nach der Regel oben.
 *
 * Nachgesehen wird im gezeichneten Blatt, vor dem ersten Bild
 * (`useLayoutEffect`), und danach bei jeder Änderung darin: Ein Blatt, das
 * erst lädt und dann ein Formular zeigt, wird zum Vollbild. **Zurück geht es
 * nicht, solange es offen ist** — ein Blatt, das zwischen zwei Formen
 * springt, weil ein Feld kurz verschwindet, wäre schlimmer als eines, das
 * etwas zu groß bleibt.
 */
export function useBlattform(
  offen: boolean,
  blatt: RefObject<HTMLElement | null>,
  grundform: Blattform,
  gezeichnet: unknown,
): Blattform {
  const [form, setzeForm] = useState<Blattform>(grundform);
  useLayoutEffect(() => {
    if (!offen) { setzeForm(grundform); return undefined; }
    if (grundform === "vollbild") { setzeForm("vollbild"); return undefined; }
    const el = blatt.current;
    if (!el) return undefined;
    const pruefen = () => {
      if (!el.querySelector(TIPPFELD)) return false;
      setzeForm("vollbild");
      return true;
    };
    if (pruefen()) return undefined;
    const beobachter = new MutationObserver(() => { if (pruefen()) beobachter.disconnect(); });
    beobachter.observe(el, { childList: true, subtree: true });
    return () => beobachter.disconnect();
  }, [offen, grundform, blatt, gezeichnet]);
  return form;
}

/**
 * Trägt das offene Blatt ein Feld, das die volle Tastatur holt (`TIPPFELD`)?
 * Nachgesehen wie in `useBlattform` — vor dem ersten Bild und bei jeder
 * Änderung darin, und auch hier ohne Rückweg, solange das Blatt offen ist.
 */
function useTippfeld(offen: boolean, blatt: RefObject<HTMLElement | null>, gezeichnet: unknown): boolean {
  const [hat, setzeHat] = useState(false);
  useLayoutEffect(() => {
    if (!offen) { setzeHat(false); return undefined; }
    const el = blatt.current;
    if (!el) return undefined;
    const pruefen = () => {
      if (!el.querySelector(TIPPFELD)) return false;
      setzeHat(true);
      return true;
    };
    if (pruefen()) return undefined;
    const beobachter = new MutationObserver(() => { if (pruefen()) beobachter.disconnect(); });
    beobachter.observe(el, { childList: true, subtree: true });
    return () => beobachter.disconnect();
  }, [offen, blatt, gezeichnet]);
  return hat;
}

/**
 * Rollt zwischen dem Finger und dem Blatt etwas, das noch nicht oben steht?
 *
 * Gefragt wird **jede** Fläche auf dem Weg und nicht nur der Leib. Ein Blatt
 * darf eine zweite Rollfläche enthalten — die Monatsliste im Zeitraum, das
 * Fenster einer Tafel —, und wer darin nach unten rollt, meint die Liste und
 * nicht das Blatt. Steht sie am oberen Anschlag, ist nach unten nichts mehr zu
 * holen; ab da gehört die Bewegung wieder dem Blatt.
 */
function rolltSchon(el: Element, grenze: Element): boolean {
  let x: Element | null = el;
  while (x) {
    if (x.scrollTop > 0) return true;
    if (x === grenze) return false;
    x = x.parentElement;
  }
  return false;
}

/**
 * Das Zeigerfangen freigeben — **auch wenn es schon weg ist** (20.09.2026).
 *
 * `releasePointerCapture` wirft `NotFoundError`, wenn die Kennung zu keinem
 * aktiven Zeiger mehr gehört — nachgemessen am 20.09.2026 in beiden Maschinen:
 *
 * | | Antwort auf `releasePointerCapture(4711)` |
 * |---|---|
 * | WebKit | `NotFoundError: The object can not be found here.` |
 * | Chrome | `NotFoundError: … No active pointer with the given id is found.` |
 *
 * Genau diese Lage ist `pointercancel`: Die Spezifikation gibt den Fang
 * **implizit** frei, bevor sie das Ereignis zustellt. Der Aufruf im Horcher traf
 * damit ins Leere und riss die ganze Behandlung ab — das Blatt blieb stehen, wo
 * der Finger es gelassen hatte, mit halb aufgehelltem Grund, und ließ sich nur
 * noch über das Kreuz schließen. Ein Versuch ringsum, und der Rest läuft weiter.
 */
function fangFreigeben(el: Element | null, zeiger: number): void {
  if (!el?.hasPointerCapture) return;
  try {
    if (el.hasPointerCapture(zeiger)) el.releasePointerCapture(zeiger);
  } catch {
    /* Schon freigegeben — dann ist hier nichts mehr zu tun. */
  }
}

/**
 * Ob zwischen Finger und Blatt eine Fläche liegt, auf der der Browser nicht
 * rollen darf (`touch-action: none`) — der Griff, der Kopf. Dort gehört die
 * Bewegung ganz dem Blatt; nur dort greift die Schwungbremse.
 */
function rollverbot(el: Element, grenze: Element): boolean {
  let x: Element | null = el;
  while (x) {
    if (getComputedStyle(x).touchAction === "none") return true;
    if (x === grenze) return false;
    x = x.parentElement;
  }
  return false;
}

/* ══════════ Die Schwungbremse (20.09.2026, Chrome auf Android) ══════════

   Luca, zum vierten Mal am selben Fehler — auch nachdem der Zug sein
   `touchend` hatte: *„Ich muss immer noch zweimal drücken."* Der Mitschrieb
   vom Telefon (`bausteine/Touchdiagnose.tsx`), diesmal mit der ganzen Kette:

       12310 tipp 281,353 → BUTTON.bvu-griff · blatt 1
       12552 los  272,695 → BUTTON.bvu-griff
       12582 blatt 1 → 0 · ovf —
       13052 ‼ KEIN KLICK · kette pd ts pu te                    ← der Zug
       14015 tipp 267,766 → A.hleiste-punkt · blatt 0
       14082 los  267,766 → A.hleiste-punkt
       14582 ‼ KEIN KLICK · kette pd ts pu te · kein mousedown   ← verloren
       14873 tipp 268,755 → path
       14964 klick path · kette pd ts pu te md mu kl             ← klappt

   Die Leiste wird getroffen, die Berührung kommt vollständig an — und
   Chrome macht keinen Tipp daraus: kein `mousedown`, kein `click`. Beide
   entstehen im Renderer erst aus der Geste `GestureTap` (Blink,
   `core/input/gesture_manager.cc`, `HandleGestureTap`); Zeiger- und
   Touch-Ereignisse laufen an ihr vorbei. Die Geste hat der Browserprozess
   geschluckt, und der Grund ist der Zug davor:

   1. Ein Finger, der mit Tempo abhebt, ist für Androids Gestenerkennung
      ein **Wurf** — `GestureFlingStart`, sobald er schneller war als die
      Mindestgeschwindigkeit des Systems, 50 dp/s
      (`ui/events/gesture_detection/gesture_detector.cc`, `OnFling`). Ein
      Zug über 340 px in 240 ms ist das allemal.
   2. **`touch-action: none` hält den Wurf nicht auf.** Der Fling-Controller
      sitzt im `InputRouterImpl` *vor* dem Touch-Action-Filter
      (`components/input/input_router_impl.cc`, `SendGestureEvent`:
      erst `PassToFlingController`, dann `FilterGestureEvent`); der Filter
      bekommt `GestureFlingStart` nie zu sehen (`touch_action_filter.cc`:
      `NOTREACHED`). Er nimmt nur die Rollbefehle, die die Kurve erzeugt.
      Die Kurve selbst läuft blind weiter, bis die Physik sie beendet
      (`fling_controller.cc`, `ProgressFling`) — eine gute Sekunde.
   3. Der nächste Finger schickt ein `GestureFlingCancel`, *„regardless of
      whether there is an ongoing fling"* (`fling_controller.cc`). Läuft
      noch eine Kurve, wird der Tipp, der es bringt, **unterdrückt** —
      `GestureTapDown` und `GestureTap` (`tap_suppression_controller.cc`:
      `LAST_CANCEL_STOPPED_FLING` → `SUPPRESSING_TAPS`). Ein Tipp in eine
      rollende Liste soll sie anhalten, nicht treffen — das ist die
      Absicht; nur rollt hier nichts.
   4. Das gilt **nur auf Android** (`gesture_configuration_android.cc`:
      `set_fling_touchscreen_tap_suppression_enabled(true)`; die Vorgabe
      ist `false`). Deshalb zeigt es weder Chrome auf dem Rechner noch der
      iOS-Simulator, und deshalb war es aus keinem Prüfstand zu holen.

   Beim Kreuz gibt es das nicht: Ein Tipp hat keinen Schwung.

   **Die Bremse:** Der Wurf wird gar nicht erst abgeschickt, wenn das
   letzte `touchmove` vor dem Abheben mit `preventDefault()` verbraucht ist
   (`touch_disposition_gesture_filter.cc`: `kScrollFlingStart` setzt das
   letzte `GestureScrollUpdate` voraus, und das ein unverbrauchtes
   `touchmove`). Das ist derselbe Weg, auf dem Seiten seit jeher das Rollen
   unterbinden — nur dass hier nichts zu unterbinden ist, weil
   `touch-action: none` das Rollen schon verbietet. Genau dort, und nur
   dort, greift sie: Wo der Browser rollen darf (der Leib, `pan-y`), bleibt
   der Schwung seiner, und eine Liste im Blatt rollt weiter wie gewohnt.

   Zwei Dinge sind dabei fest:
   * **Nicht über React.** React hängt `touchmove` passiv an; ein
     `preventDefault()` darin ist wirkungslos. Der Horcher hängt nativ am
     Blatt, `{ passive: false }`.
   * **Er hängt nur während des Zuges**, von `pointerdown` bis `pointerup`.
     Chrome stellt Berührungen aus einer `touch-action: none`-Fläche
     ohnehin blockierend und damit abbrechbar zu (`input_handler_proxy.cc`,
     `HitTestTouchEvent`: *„DidNotHandle due to no touchaction"*); ein
     ständiger Horcher machte dagegen den ganzen Leib blockierend und
     kostete jede Liste im Blatt einen Umweg über den Hauptfaden.

   Ob es sitzt, zeigt die Diagnose: Der Zug trägt `tm…!` in seiner Kette,
   der Tipp danach `md mu kl`.
   ═══════════════════════════════════════════════════════════════════ */
function schwungBremsen(blatt: HTMLElement, el: Element): (() => void) | null {
  if (!rollverbot(el, blatt)) return null;
  const bremsen = (t: TouchEvent) => { if (t.cancelable) t.preventDefault(); };
  blatt.addEventListener("touchmove", bremsen, { passive: false });
  return () => blatt.removeEventListener("touchmove", bremsen);
}

/**
 * Das Herunterziehen — für jedes Blatt, das von unten kommt.
 *
 * `nurAmGriff`: Im Vollbild zieht nur der Griff. Wer ein Formular über den
 * ganzen Schirm oben anfasst, will rollen oder tippen, nicht schließen.
 */
export function useZiehen({ blatt, schirm, zu, nurAmGriff = false }: {
  blatt: RefObject<HTMLElement | null>;
  schirm: RefObject<HTMLElement | null>;
  /**
   * Die Rollfläche des Blattes.
   *
   * **Wird nicht mehr gebraucht** — gefragt wird seit dem 18.09.2026 jede
   * Fläche zwischen Finger und Blatt (`rolltSchon`), und der Leib ist eine
   * davon. Die Eigenschaft steht noch in der Signatur, weil das Deckblatt sie
   * setzt; sie darf weg, wenn dort das nächste Mal etwas geändert wird.
   */
  leib?: RefObject<HTMLElement | null>;
  zu: () => void;
  nurAmGriff?: boolean;
}) {
  const zug = useRef<Zug | null>(null);

  /**
   * Das Blatt an seinen Platz oder um `weg` Pixel nach unten.
   *
   * **Nur schreiben, nie lesen** (21.09.2026). Die Höhe kommt vom Aufrufer
   * (`zug.current.hoehe`, einmal beim Anfassen gemessen); sie hier aus dem
   * DOM zu holen hieße, mitten zwischen zwei Schreibvorgängen ein Layout zu
   * erzwingen — je Bild einmal, solange der Finger sich bewegt.
   *
   * `hoehe = 0` heißt „unbekannt": Dann bleibt der Grund, wie er ist, statt
   * mit einer geratenen Zahl zu springen.
   */
  const setzen = useCallback((weg: number, weich: boolean, hoehe = 0) => {
    const b = blatt.current;
    const s = schirm.current;
    if (!b) return;
    /* **Die Eingangsanimation muss weg, sobald jemand zieht.** `.bvu-blatt`
       fährt mit `animation: bvu-herein 200ms` herein, und eine laufende
       CSS-Animation schlägt in der Kaskade den Inline-Stil — wer in diesen
       200 ms anfasst, sah beide gegeneinander arbeiten. Das ist das
       „manchmal" am Flackern: Es trifft nur den, der sofort zieht.

       `animation: none` steht hier und nicht in der Stildatei, weil es
       genau so lange gilt, wie das Blatt in der Hand ist. */
    if (b.style.animation !== "none") b.style.animation = "none";
    b.style.transition = weich ? "transform 180ms ease-out" : "none";
    b.style.transform = weg > 0 ? `translateY(${weg}px)` : "";
    // Der Grund wird heller, während das Blatt geht — sonst sieht es aus,
    // als schöbe sich das Blatt hinter einen Vorhang, der bleibt.
    if (s && (weg === 0 || hoehe > 0)) {
      s.style.opacity = weg > 0 ? String(Math.max(0.25, 1 - (weg / hoehe) * 0.9)) : "";
    }
  }, [blatt, schirm]);

  const darfZiehen = useCallback((el: Element, amGriff: boolean): boolean => {
    /* ── Ein Blatt zieht nur an sich selbst (18.09.2026) ──

       Luca, nach dem Durchklicken: *„Wenn ich den Zeitraum runterscrolle,
       wird auch das dahinterliegende Bottom Sheet geschlossen."*

       **Der Grund ist der Portal-Baum von React.** Ein Blatt hängt im
       `#maskenraum` und nicht dort, wo es geschrieben steht — im DOM sind
       zwei gestapelte Blätter Geschwister. **Ereignisse steigen aber den
       React-Baum hinauf und nicht den DOM-Baum**, und das Zeitraum-Blatt
       steht im Quelltext innerhalb des Nachschlagen-Blattes. Jede Berührung
       darin kam damit auch hier an: Die Prüfung darunter sah einen Finger
       „irgendwo im Nichts" — die Monatsliste liegt in einem fremden Leib,
       also griff weder `EIGENE SACHE` noch der Rollstand —, und das untere
       Blatt fing an mitzulaufen. Ein schneller Wisch war dann Schwung genug,
       und es ging zu, während man nur einen Monat weiter wollte.

       Eine Zeile stellt es ab: Wer nicht in diesem Blatt liegt, zieht nicht
       an ihm. Damit kann die Geste auch nie zwei Blätter auf einmal
       schließen — jedes hört nur auf seine eigene Fläche, und die
       Schließanfrage (`schale/schliessanfrage.ts`) gibt ohnehin nur dem
       obersten recht. */
    const b = blatt.current;
    if (!b || !b.contains(el)) return false;
    if (amGriff) return true;
    if (nurAmGriff) return false;
    if (el.closest(EIGENE_SACHE)) return false;
    // Ein Knopf gehört sich selbst — außer, er IST der Griff.
    if (el.closest("button") && !el.closest(".bvu-griff")) return false;
    // Eine Rollfläche, die schon gerollt ist, rollt weiter; gezogen wird erst
    // wieder, wenn sie am oberen Anschlag steht.
    if (rolltSchon(el, b)) return false;
    return true;
  }, [blatt, nurAmGriff]);

  const anfassen = useCallback((e: PointerEvent<HTMLElement>) => {
    /* **Ein Zug, der nie losgelassen wurde, sperrt das Blatt nicht** (20.09.2026).
       Bis zur Schwelle ist der Zeiger noch nicht gefangen; hebt der Finger
       außerhalb des Blattes ab, kommt `pointerup` nie hier an und `zug` bliebe
       für immer stehen — danach nähme `anfassen` keine Berührung mehr an, und
       das Blatt ließe sich nicht mehr herunterziehen. Ein laufender Zug behält
       seinen Vorrang (ein zweiter Finger stört ihn nicht); ein liegengebliebener
       wird überschrieben. */
    if (zug.current?.zieht) return;
    // Ein liegengebliebener Zug gibt zuerst seine Bremse frei — sonst hielte
    // sie den Leib fest, obwohl niemand mehr zieht.
    zug.current?.bremse?.();
    zug.current = null;
    const el = e.target as Element;
    const amGriff = !!el.closest(".bvu-griff");
    if (!darfZiehen(el, amGriff)) return;
    zug.current = {
      zeiger: e.pointerId,
      start: e.clientY,
      zuletzt: e.clientY,
      wann: e.timeStamp,
      tempo: 0,
      weg: 0,
      zieht: false,
      /* **Einmal messen, dann nur noch schreiben** (21.09.2026). Jetzt ist
         der richtige Augenblick: Der Finger liegt auf, gerendert ist alles,
         und bis er wieder loslässt ändert sich an der Höhe nichts. */
      hoehe: blatt.current?.offsetHeight ?? 0,
      // Nur der Finger hat Schwung, den der Browser weiterträgt (siehe oben).
      bremse: e.pointerType === "touch" ? schwungBremsen(e.currentTarget, el) : null,
    };
  }, [blatt, darfZiehen]);

  const ziehen = useCallback((e: PointerEvent<HTMLElement>) => {
    const z = zug.current;
    if (!z || z.zeiger !== e.pointerId) return;
    const weg = Math.max(0, e.clientY - z.start);
    const dauer = Math.max(1, e.timeStamp - z.wann);
    z.tempo = (e.clientY - z.zuletzt) / dauer;
    z.zuletzt = e.clientY;
    z.wann = e.timeStamp;
    z.weg = weg;
    if (!z.zieht && weg < SCHWELLE_ZUG) return;
    if (!z.zieht) {
      z.zieht = true;
      // Ab jetzt gehört die Bewegung dem Blatt — auch wenn der Finger
      // dabei über die Kante hinausfährt.
      e.currentTarget.setPointerCapture?.(e.pointerId);
    }
    setzen(weg, false, z.hoehe);
  }, [setzen]);

  const loslassen = useCallback((e: PointerEvent<HTMLElement>) => {
    const z = zug.current;
    if (!z || z.zeiger !== e.pointerId) return;
    zug.current = null;
    // `pointerup` kommt vor `touchend`, und danach bewegt sich nichts mehr:
    // Die Bremse hat ihren Dienst getan.
    z.bremse?.();
    if (!z.zieht) return;
    fangFreigeben(e.currentTarget, e.pointerId);
    const ruhig = typeof window !== "undefined"
      && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    /* **Abgebrochen ist nicht losgelassen** (20.09.2026). `pointercancel`
       kommt, wenn der Browser sich die Bewegung nimmt — auf dem Kopf und im
       Leib, wo `touch-action` das Rollen noch zulässt. Das ist keine Auskunft
       darüber, was der Mensch wollte; ein Blatt, das dabei zugeht, geht zu,
       ohne dass jemand es losgelassen hat. Es federt zurück. */
    if (e.type === "pointercancel") { setzen(0, !ruhig); return; }
    // Die beim Anfassen gemessene Höhe — dieselbe, mit der gezogen wurde.
    const hoehe = z.hoehe;
    const weitGenug = hoehe > 0 && z.weg > hoehe * ANTEIL_ZU;
    if (weitGenug || z.tempo > SCHWUNG_ZU) {
      /* ── Erst die Berührung zu Ende bringen, dann das Blatt nehmen ──
         (20.09.2026, gemessen auf Lucas Gerät)

         Luca, dreimal am selben Fehler: *„Ich muss immer noch zweimal
         drücken … wenn ich das über das X schließe, habe ich das Problem
         nicht."* Der Mitschrieb vom Telefon
         (`bausteine/Touchdiagnose.tsx`) hat es aufgedeckt, und es lag
         nicht an der Tastatur und nicht an einem verdeckenden Blatt:

             18251 tipp 210,354 → SPAN.bvu-griff-balken · blatt 1
             18614 los  258,791 → —
             19114 ‼ KEIN KLICK · kette pd ts pu      ← kein touchend!
             19384 tipp 271,755 → svg · blatt 0
             19964 ‼ KEIN KLICK · kette pd ts pu te · kein mousedown

         **Dem Zug fehlte das `touchend`.** `zu()` lief mitten im
         `pointerup`, React nahm das Blatt noch im selben Durchlauf aus
         dem Baum — und als Chrome gleich darauf das `touchend` zustellen
         wollte, war das Element unter dem Finger weg. Das Ereignis fiel
         aus. Ein Bild Abstand genügt und ist nachgemessen:

             sofort entfernt    Zug: pdown tstart pup        ← tend fehlt
             ein Bild später    Zug: pdown tstart pup tend   ← vollständig

         **Das zweite Drücken war es trotzdem nicht.** Mit dem `touchend`
         blieb der Fehler, und der nächste Mitschrieb zeigte, warum: Der
         Zug ist für Chrome ein Wurf, und den Tipp in einen laufenden Wurf
         unterdrückt Android — siehe „Die Schwungbremse" oben
         (`schwungBremsen`). Das Bild Abstand bleibt trotzdem: Eine
         Berührung, die ihr Ende nie bekommt, ist ein Loch, auch wenn es
         nicht dieses war.

         `setzen` steht mit im selben Bild: Zusammen sind Rückstellung und
         Verschwinden ein einziger Schritt wie bisher. Stünde es davor,
         spränge das Blatt für ein Bild an seinen Platz zurück, bevor es
         geht. Bleibt das Blatt stehen, weil eine Frage nach dem Entwurf
         kommt, stellt dieselbe Zeile es wieder an seinen Platz. */
      requestAnimationFrame(() => { setzen(0, false); zu(); });
      return;
    }
    setzen(0, !ruhig);
  }, [blatt, setzen, zu]);

  return { anfassen, ziehen, loslassen, setzen };
}
